import { FormikErrors } from 'formik';
import i18next from 'i18next';
import toast from 'react-hot-toast';
import { IErrorsResponse } from 'types/service/errorCodes.interface';

type OutputObject = {
  [key: string]: any;
};

const transformResponseErrorsToFormikErrors = <Form>(
  errors: IErrorsResponse
): FormikErrors<Form> => {
  const result: OutputObject = {};

  errors.forEach(({ errorCode, property }) => {
    const keys = property?.split('.') ?? [];
    let currentLevel = result;

    keys.forEach((key, index) => {
      const keyLowerCase = `${key[0].toLowerCase()}${key.slice(1)}`;
      if (index === keys.length - 1) {
        currentLevel[keyLowerCase] = i18next.t(`error-code.${errorCode}`);
      } else {
        if (!currentLevel[keyLowerCase]) {
          currentLevel[keyLowerCase] = {};
        }
        currentLevel = currentLevel[keyLowerCase];
      }
    });
  });

  return result as FormikErrors<Form>;
};

interface IHandleErrorsResponse<Form> {
  formik: FormikErrors<Form>;
  toasts: string[];
}

export const handleErrorsResponse = <Form>(
  errorMessage: string
): IHandleErrorsResponse<Form> => {
  const errors: IErrorsResponse = JSON.parse(errorMessage);

  const errorsWithProperty = errors.filter(error => error.property !== null);
  const errorsWithoutProperty = errors.filter(error => error.property === null);

  const formikErrors =
    transformResponseErrorsToFormikErrors<Form>(errorsWithProperty);
  const toastErrors = errorsWithoutProperty.length
    ? errorsWithoutProperty.map(error =>
        i18next.t(`error-code.${error.errorCode}`)
      )
    : errors.length
    ? [i18next.t('error-code.FORM_ERROR')]
    : [];

  return { formik: formikErrors, toasts: toastErrors };
};

export const onErrorShowAllErrorsToast = (
  error: Error,
  emptyToastsMessage?: string,
  maxErrorsDisplay = 3
) => {
  const errors: IErrorsResponse = JSON.parse(error.message);
  errors.length
    ? errors.map(
        ({ errorCode }, key) =>
          key < maxErrorsDisplay &&
          toast.error(i18next.t(`error-code.${errorCode}`))
      )
    : emptyToastsMessage && toast.error(i18next.t(emptyToastsMessage));
};

export const onErrorShowToast = (error: Error, emptyToastsMessage?: string) => {
  const { toasts } = handleErrorsResponse(error.message);

  !!toasts.length
    ? toasts.map(message => toast.error(message))
    : emptyToastsMessage && toast.error(i18next.t(emptyToastsMessage));
};
